import { defineStore } from "pinia";
import {
  LocationsStore,
  CreateLocation,
  UpdateLocation,
  LocationSummary,
} from "@/terminals/types/locations";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import axios from "@/app/plugins/axios";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "@/app/plugins/firebase";
import { useAccountStore } from "@/account/stores/account";
import { getErrorMessage } from "@/shared/utils/errorMessage";

const STORAGE_KEY = "location-store:selected";
const DEFAULT_PAGE_SIZE = 10;
let activeLocationsUnsubscribe: (() => void) | null = null;

const readSelectionsFromStorage = (storage: Storage | null): Record<string, string> | null => {
  if (!storage) return null;
  try {
    const rawSelections = storage.getItem(STORAGE_KEY);
    if (!rawSelections) return null;
    const parsed = JSON.parse(rawSelections);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, string>;
    }
  } catch {
    return null;
  }
  return null;
};

const getStorageSafely = (type: "localStorage" | "sessionStorage") => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window[type];
  } catch {
    return null;
  }
};

const getPersistedSelectionsFromStorage = (): Record<string, string> => {
  const localSelections = readSelectionsFromStorage(getStorageSafely("localStorage"));
  if (localSelections) {
    return localSelections;
  }

  const sessionSelections = readSelectionsFromStorage(getStorageSafely("sessionStorage"));
  if (sessionSelections) {
    return sessionSelections;
  }

  return {};
};

export const useLocationStore = defineStore("location", {
  state: (): LocationsStore => ({
    globalLocations: { locations: [] },
    paginatedLocations: {
      locations: [],
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      total: 0,
    },
    selectedLocation: null,
    loading: false,
    paginatedLoading: false,
    unsubscribe: null,
    persistedSelections: {},
  }),
  hydrate(storeState: LocationsStore) {
    storeState.persistedSelections = getPersistedSelectionsFromStorage();
  },
  getters: {
    isLoading: (state) => state.loading,
    getLocations: (state) => state.globalLocations,
    getSelectedLocation: (state) => state.selectedLocation,
  },
  actions: {
    setSelectedLocation(
      location: LocationSummary | null,
      options: { persist?: boolean } = {},
    ): void {
      this.selectedLocation = location ? { ...location } : null;
      const shouldPersist = options.persist !== false;
      const accountId = useAccountStore().account?.id;
      if (shouldPersist && accountId) {
        this.persistSelection(accountId, location?.id ?? null);
      }
    },
    persistSelection(accountId: string, locationId: string | null): void {
      if (!accountId) {
        return;
      }

      if (locationId) {
        if (this.persistedSelections[accountId] === locationId) {
          return;
        }
        this.persistedSelections = {
          ...this.persistedSelections,
          [accountId]: locationId,
        };
      } else if (accountId in this.persistedSelections) {
        const { [accountId]: _removed, ...rest } = this.persistedSelections;
        this.persistedSelections = rest;
      }

      this.saveSelectionsToStorage();
    },
    saveSelectionsToStorage(): void {
      if (typeof window === "undefined") {
        return;
      }
      const payload = JSON.stringify(this.persistedSelections);
      try {
        localStorage.setItem(STORAGE_KEY, payload);
        return;
      } catch {
        // fall through to session storage
      }
      try {
        sessionStorage.setItem(STORAGE_KEY, payload);
      } catch {
        // Unable to persist selection - ignore
      }
    },
    startLocationsListener(accountId?: string): void {
      const activeAccountId = accountId ?? useAccountStore().account?.id;

      if (!activeAccountId) {
        return;
      }

      this.stopLocationsListener();
      this.loading = true;

      const locationsRef = collection(db, "accounts", activeAccountId, "locations");

      const firebaseQuery = query(locationsRef, where("deleted", "==", false));

      this.unsubscribe = onSnapshot(
        firebaseQuery,
        (snapshot) => {
          const locationList = snapshot.docs.map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              }) as LocationSummary,
          );

          this.globalLocations.locations = locationList;
          this.loading = false;
          this.ensureSelectedLocation(activeAccountId);
        },
        (error) => {
          this.loading = false;
          console.error("Failed to load locations from Firestore", error);
          useSnackBarStore().toastError({
            message: error?.message || "Failed to load locations",
          });
          this.stopLocationsListener();
          this.listLocations(false);
        },
      );
      activeLocationsUnsubscribe = this.unsubscribe;
    },
    stopLocationsListener(): void {
      if (activeLocationsUnsubscribe) {
        activeLocationsUnsubscribe();
        activeLocationsUnsubscribe = null;
      }
      this.unsubscribe = null;
      this.loading = false;
    },
    ensureSelectedLocation(accountId?: string): void {
      const locationList = this.globalLocations?.locations || [];

      if (!locationList.length) {
        this.selectedLocation = null;
        if (accountId) {
          this.persistSelection(accountId, null);
        }
        return;
      }

      if (!Object.keys(this.persistedSelections || {}).length) {
        this.persistedSelections = getPersistedSelectionsFromStorage();
      }

      const persistedId = accountId ? this.persistedSelections[accountId] : undefined;
      if (persistedId) {
        const persisted = locationList.find((location) => location.id === persistedId);
        if (persisted) {
          this.setSelectedLocation(persisted);
          return;
        }
      }

      const currentId = this.selectedLocation?.id;
      if (currentId) {
        const current = locationList.find((location) => location.id === currentId);
        if (current) {
          this.setSelectedLocation(current);
          return;
        }
      }

      if (persistedId) {
        this.setSelectedLocation(locationList[0], { persist: false });
        return;
      }

      this.setSelectedLocation(locationList[0]);
    },
    setConfigurationLocation(configurationId: string): void {
      if (!configurationId || !this.selectedLocation) return;
      this.selectedLocation.configuration_overrides = configurationId;
    },
    async createLocation(payload: CreateLocation): Promise<LocationSummary | undefined> {
      try {
        const response = await axios.post<LocationSummary>("/location", payload);
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return undefined;
      }
    },
    async updateLocation(payload: UpdateLocation): Promise<void> {
      try {
        const response = await axios.put<LocationSummary>(`/location/${payload.id}`, payload);
        this.globalLocations.locations.push(response.data);
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      }
    },
    async deleteLocation(locationId: string): Promise<boolean> {
      if (!locationId) {
        return false;
      }
      try {
        await axios.delete(`/location/${locationId}`);
        const index = this.globalLocations.locations.findIndex(
          (location) => location.id === locationId,
        );
        if (index !== -1) {
          this.globalLocations.locations.splice(index, 1);
        }
        return true;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return false;
      }
    },
    async listLocations(
      deleted?: boolean,
      includeCounts = false,
      scope: "global" | "paginated" = "global",
    ): Promise<LocationSummary[] | undefined> {
      const isPaginatedScope = scope === "paginated";
      try {
        if (isPaginatedScope) {
          this.paginatedLoading = true;
        } else {
          this.loading = true;
        }
        const response = await axios.get<LocationSummary[]>("/location", {
          params: {
            deleted: deleted,
            includeCounts,
          },
        });
        if (isPaginatedScope) {
          const data = Array.isArray(response.data) ? response.data : [];
          this.paginatedLocations = {
            ...this.paginatedLocations,
            locations: data,
            total: data.length,
          };
        } else {
          this.globalLocations.locations = response.data;
          this.ensureSelectedLocation(useAccountStore().account?.id);
        }
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return undefined;
      } finally {
        if (isPaginatedScope) {
          this.paginatedLoading = false;
        } else {
          this.loading = false;
        }
      }
    },

    async retrieveLocations(locationId: string): Promise<LocationSummary | undefined> {
      try {
        this.loading = true;
        const response = await axios.get<LocationSummary>(`/location/${locationId}`);
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return undefined;
      } finally {
        this.loading = false;
      }
    },

    async refreshSelectedLocation(locationId?: string | null): Promise<LocationSummary | null> {
      if (!locationId) {
        this.setSelectedLocation(null);
        return null;
      }

      const location = await this.retrieveLocations(locationId);
      if (location) {
        this.setSelectedLocation(location);
      }

      return location ?? null;
    },
  },
});
